import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, WorkflowIcon, ListChecksIcon, CalendarIcon, LayersIcon, FileTextIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
/* ─────── Section TOC Nav ─────── */
export function PrdSectionNav() {
  const sections = [{
    id: 'promise',
    label: 'Promise',
    num: '0'
  }, {
    id: 'foundations',
    label: 'Foundations',
    num: '·'
  }, {
    id: 'roles',
    label: 'Roles',
    num: '1'
  }, {
    id: 'jobs',
    label: 'Jobs',
    num: '2'
  }, {
    id: 'rules',
    label: 'Rules',
    num: '3'
  }, {
    id: 'data',
    label: 'Data',
    num: '4'
  }, {
    id: 'video',
    label: 'Video',
    num: '5'
  }, {
    id: 'execution',
    label: 'Roadmap',
    num: '6'
  }, {
    id: 'ai-layer',
    label: 'AI Layer',
    num: '7'
  }];
  const [active, setActive] = useState<string>('promise');
  useEffect(() => {
    const handler = () => {
      const scrollY = window.scrollY + 200;
      let current = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.offsetTop <= scrollY) current = s.id;
      }
      setActive(current);
    };
    handler();
    window.addEventListener('scroll', handler, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <nav className="border-t border-slate-100 bg-white/80 backdrop-blur-sm" data-id="element-5143">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-5144">
        <div className="flex items-center gap-1 overflow-x-auto py-2" data-id="element-5145">
          {sections.map(s => {
          const isActive = active === s.id;
          return <a key={s.id} href={`#${s.id}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${isActive ? 'bg-brand-navy text-white shadow-sm' : 'text-slate-500 hover:text-brand-navy hover:bg-slate-100'}`} data-id="element-5146">
                <span className={`text-[10px] ${isActive ? 'text-brand-teal' : 'text-slate-400'}`} data-id="element-5147">
                  {s.num}
                </span>
                <span data-id="element-5148">{s.label}</span>
              </a>;
        })}
        </div>
      </div>
    </nav>;
}
/* ─────── Executive Summary / TL;DR ─────── */
export function PrdExecutiveSummary() {
  const cards = [{
    eyebrow: 'The Bet',
    title: 'Event Intelligence Platform',
    body: 'Hotels host every kind of event. We add the AI planning, digital hub, and follow-up layer for all of them — corporate is the money lane, social is the goodwill lane.',
    icon: SparklesIcon,
    color: 'bg-brand-navy text-white',
    accent: 'text-brand-teal'
  }, {
    eyebrow: 'Workflow #1',
    title: '★ Book of Business Upload',
    body: 'Sales Manager drags in their PDF / CSV book of business. We auto-tag each booking, recommend a package, and generate proposals. Ships in 20 days.',
    icon: WorkflowIcon,
    color: 'bg-brand-coral text-white',
    accent: 'text-yellow-200'
  }, {
    eyebrow: 'Build Order',
    title: 'P1 → P2 → P3 → P4',
    body: 'Hotel Sales Manager, then Coordinator, then Client, then Attendee. Phase-2 roles (Admin, Platform) follow. Facilitators and Sponsors are deferred.',
    icon: ListChecksIcon,
    color: 'bg-white text-brand-navy border border-slate-200',
    accent: 'text-brand-coral'
  }, {
    eyebrow: 'Timeline',
    title: '14 days · May 15 → May 28',
    body: 'One two-week sprint. Workflow #1 — Sales Manager uploads a book of business and walks out with a client-ready proposal — ships end-to-end by Fri May 28.',
    icon: CalendarIcon,
    color: 'bg-white text-brand-navy border border-slate-200',
    accent: 'text-brand-teal'
  }, {
    eyebrow: 'Stack',
    title: 'Boring tables · Smart layer',
    body: 'Supabase + 15 core tables under the hood. Mux for video. NotebookLM + Sanity layered on top once the operational core is solid.',
    icon: LayersIcon,
    color: 'bg-white text-brand-navy border border-slate-200',
    accent: 'text-pink-500'
  }];
  return <section id="tldr" className="py-16 px-6 md:px-12 bg-gradient-to-b from-white to-slate-50 border-b border-slate-100 scroll-mt-32" data-id="element-5149">
      <div className="max-w-7xl mx-auto" data-id="element-5150">
        <div className="text-center mb-10" data-id="element-5151">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-coral/10 text-brand-coral text-xs font-bold uppercase tracking-wider mb-4" data-id="element-5152">
            <FileTextIcon className="w-3.5 h-3.5" data-id="element-5153" />
            TL;DR · Read This First
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3 leading-tight" data-id="element-5154">
            The whole doc in five cards.
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-id="element-5155">
            What we're building, what ships first, in what order, on what
            timeline, on what stack.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-5156">
          {cards.map((c, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.06
        }} className={`${c.color} rounded-2xl p-5 shadow-sm flex flex-col`} data-id="element-5157">
              <div className={`flex items-center gap-1.5 mb-3 ${c.accent}`} data-id="element-5158">
                <c.icon className="w-3.5 h-3.5" data-id="element-5159" />
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-5160">
                  {c.eyebrow}
                </span>
              </div>
              <h3 className="text-base font-bold mb-2 leading-tight" data-id="element-5161">
                {c.title}
              </h3>
              <p className="text-xs leading-relaxed opacity-90" data-id="element-5162">{c.body}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ─────── Execution Gantt · 14-day MVP sprint (May 15 → May 28) ─────── */
type GanttTask = {
  id: string;
  lane: string;
  laneTag: string;
  laneColor: string;
  barColor: string;
  label: string;
  startDay: number; // 0..13 (May 15 = 0)
  duration: number; // in days
  owner: string;
  star?: boolean;
  deps?: string[];
};
export function ExecutionGantt() {
  // 14 days from May 15 → May 28
  const startDate = new Date(2026, 4, 15); // May 15, 2026 (Fri)
  const TOTAL_DAYS = 14;
  const TODAY_DAY = 5; // May 20 = index 5
  const days = Array.from({
    length: TOTAL_DAYS
  }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return {
      index: i,
      date: d.getDate(),
      day: d.toLocaleDateString('en-US', {
        weekday: 'short'
      }),
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
      isToday: i === TODAY_DAY,
      isPast: i < TODAY_DAY
    };
  });
  const tasks: GanttTask[] = [
  // Lane 1 · Foundation (Days 1-3)
  {
    id: 'supabase',
    lane: 'Foundation',
    laneTag: 'Schema',
    laneColor: 'bg-slate-700',
    barColor: 'bg-slate-600',
    label: 'Supabase · core tables · auth',
    startDay: 0,
    duration: 2,
    owner: 'Alex'
  }, {
    id: 'seed',
    lane: 'Foundation',
    laneTag: 'Schema',
    laneColor: 'bg-slate-700',
    barColor: 'bg-slate-500',
    label: 'Seed data · RLS policies',
    startDay: 2,
    duration: 1,
    owner: 'Alex',
    deps: ['supabase']
  },
  // Lane 2 · Upload (Days 3-6) ★ Workflow #1
  {
    id: 'upload-ui',
    lane: 'Book of Business Upload',
    laneTag: '★ Workflow #1',
    laneColor: 'bg-brand-coral',
    barColor: 'bg-brand-coral',
    label: '★ Drag-and-drop upload UI',
    startDay: 3,
    duration: 2,
    owner: 'Alex',
    star: true,
    deps: ['supabase']
  }, {
    id: 'parser',
    lane: 'Book of Business Upload',
    laneTag: '★ Workflow #1',
    laneColor: 'bg-brand-coral',
    barColor: 'bg-brand-coral/80',
    label: 'PDF / CSV parser · OpenAI extract',
    startDay: 4,
    duration: 3,
    owner: 'Alex',
    deps: ['upload-ui']
  },
  // Lane 3 · Tagging (Days 6-8)
  {
    id: 'classifier',
    lane: 'Event Tagging',
    laneTag: 'AI Classify',
    laneColor: 'bg-purple-600',
    barColor: 'bg-purple-600',
    label: 'Event-type classifier · confidence score',
    startDay: 6,
    duration: 2,
    owner: 'Alex',
    deps: ['parser']
  }, {
    id: 'review',
    lane: 'Event Tagging',
    laneTag: 'AI Classify',
    laneColor: 'bg-purple-600',
    barColor: 'bg-purple-500',
    label: 'Review screen · accept / override',
    startDay: 7,
    duration: 2,
    owner: 'Alex',
    deps: ['classifier']
  },
  // Lane 4 · Package picker (Days 8-10)
  {
    id: 'packages',
    lane: 'Package Recommendation',
    laneTag: 'Rules + LLM',
    laneColor: 'bg-brand-teal',
    barColor: 'bg-brand-teal',
    label: 'Package picker · recommend + lock',
    startDay: 8,
    duration: 2,
    owner: 'Alex',
    deps: ['classifier']
  },
  // Lane 5 · Proposal (Days 9-12)
  {
    id: 'proposal',
    lane: 'AI Proposal',
    laneTag: 'AI Output',
    laneColor: 'bg-amber-500',
    barColor: 'bg-amber-500',
    label: 'AI proposal generator · client-ready',
    startDay: 9,
    duration: 3,
    owner: 'Alex',
    deps: ['packages']
  }, {
    id: 'send',
    lane: 'AI Proposal',
    laneTag: 'AI Output',
    laneColor: 'bg-amber-500',
    barColor: 'bg-amber-400',
    label: 'Send + track open',
    startDay: 11,
    duration: 1,
    owner: 'Alex',
    deps: ['proposal']
  },
  // Lane 6 · Demo / Ship (Days 12-14)
  {
    id: 'qa',
    lane: 'Demo & Ship',
    laneTag: 'Milestone',
    laneColor: 'bg-emerald-600',
    barColor: 'bg-emerald-600',
    label: 'Internal QA · staging deploy',
    startDay: 11,
    duration: 2,
    owner: 'Alex + Ash',
    deps: ['send']
  }, {
    id: 'demo',
    lane: 'Demo & Ship',
    laneTag: 'Milestone',
    laneColor: 'bg-emerald-600',
    barColor: 'bg-emerald-500',
    label: '★ Friday demo · Lamplighter walk-through',
    startDay: 13,
    duration: 1,
    owner: 'Ash',
    star: true,
    deps: ['qa']
  }];
  // Group tasks by lane preserving order
  const laneOrder: string[] = [];
  const laneMap = new Map<string, GanttTask[]>();
  tasks.forEach(t => {
    if (!laneMap.has(t.lane)) {
      laneMap.set(t.lane, []);
      laneOrder.push(t.lane);
    }
    laneMap.get(t.lane)!.push(t);
  });
  // Constants for layout (px)
  const LANE_LABEL_W = 200;
  const DAY_W = 56;
  const ROW_H = 36;
  const ROW_GAP = 6;
  const totalGridW = DAY_W * TOTAL_DAYS;
  // Sum rows per lane for vertical placement
  let rowCounter = 0;
  const laneRows: {
    lane: string;
    startRow: number;
    rows: number;
  }[] = [];
  laneOrder.forEach(lane => {
    const rows = laneMap.get(lane)!.length;
    laneRows.push({
      lane,
      startRow: rowCounter,
      rows
    });
    rowCounter += rows;
  });
  const totalRows = rowCounter;
  const gridH = totalRows * (ROW_H + ROW_GAP);
  return <section id="execution" className="py-20 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100 scroll-mt-32" data-id="element-5163">
      <div className="max-w-7xl mx-auto" data-id="element-5164">
        <div className="flex items-start gap-4 mb-10" data-id="element-5165">
          <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0 font-bold text-lg" data-id="element-5166">
            6
          </div>
          <div className="flex-1" data-id="element-5167">
            <div className="flex items-center gap-2 text-brand-coral text-xs font-bold uppercase tracking-wider mb-2" data-id="element-5168">
              <CalendarIcon className="w-4 h-4" data-id="element-5169" />
              Execution Plan · 14 Days · May 15 → May 28
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-5170">
              Ship Workflow #1 in two weeks.
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl" data-id="element-5171">
              One sprint. One outcome. By Friday May 28, the Sales Manager can
              drag in a book of business and walk out with a client-ready
              proposal. Everything else is downstream of this.
            </p>
          </div>
        </div>

        {/* Scope summary cards */}
        <div className="grid md:grid-cols-4 gap-3 mb-8" data-id="element-5172">
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-id="element-5173">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1" data-id="element-5174">
              Start
            </div>
            <div className="text-lg font-bold text-brand-navy" data-id="element-5175">Thu May 15</div>
            <div className="text-xs text-slate-500" data-id="element-5176">Foundation + Schema</div>
          </div>
          <div className="bg-white rounded-xl border-2 border-brand-coral/30 p-4 relative" data-id="element-5177">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-coral mb-1" data-id="element-5178">
              Today · Day 6
            </div>
            <div className="text-lg font-bold text-brand-navy" data-id="element-5179">Tue May 20</div>
            <div className="text-xs text-slate-500" data-id="element-5180">Parser in progress</div>
            <motion.span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-brand-coral" animate={{
            opacity: [1, 0.3, 1]
          }} transition={{
            duration: 1.6,
            repeat: Infinity
          }} data-id="element-5181" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-id="element-5182">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1" data-id="element-5183">
              Demo
            </div>
            <div className="text-lg font-bold text-brand-navy" data-id="element-5184">Fri May 28</div>
            <div className="text-xs text-slate-500" data-id="element-5185">
              Live walk-through with Lamplighter
            </div>
          </div>
          <div className="bg-brand-navy text-white rounded-xl p-4" data-id="element-5186">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-1" data-id="element-5187">
              Outcome
            </div>
            <div className="text-lg font-bold leading-tight" data-id="element-5188">
              Book → Proposal
            </div>
            <div className="text-xs text-slate-300 mt-1" data-id="element-5189">
              End-to-end Workflow #1
            </div>
          </div>
        </div>

        {/* Gantt */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-id="element-5190">
          <div className="p-5" style={{
          minWidth: `${LANE_LABEL_W + totalGridW + 40}px`
        }} data-id="element-5191">
            {/* Day header */}
            <div className="flex mb-3" data-id="element-5192">
              <div style={{
              width: LANE_LABEL_W
            }} className="shrink-0" data-id="element-5193">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-5194">
                  Workstream
                </div>
              </div>
              <div className="flex" style={{
              width: totalGridW
            }} data-id="element-5195">
                {days.map(d => <div key={d.index} style={{
                width: DAY_W
              }} className={`text-center px-1 py-1 rounded-md ${d.isToday ? 'bg-brand-coral text-white' : d.isWeekend ? 'bg-slate-50 text-slate-400' : 'text-slate-500'}`} data-id="element-5196">
                    <div className="text-[9px] font-bold uppercase tracking-wider opacity-80" data-id="element-5197">
                      {d.day}
                    </div>
                    <div className="text-sm font-bold leading-none mt-0.5" data-id="element-5198">
                      {d.date}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Grid + bars container */}
            <div className="flex" data-id="element-5199">
              {/* Lane labels column */}
              <div style={{
              width: LANE_LABEL_W
            }} className="shrink-0 pr-3" data-id="element-5200">
                {laneRows.map(({
                lane,
                startRow,
                rows
              }) => {
                const first = laneMap.get(lane)![0];
                return <div key={lane} style={{
                  height: rows * (ROW_H + ROW_GAP)
                }} className="flex items-center" data-id="element-5201">
                      <div className="w-full" data-id="element-5202">
                        <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${first.laneColor} mb-1`} data-id="element-5203">
                          {first.laneTag}
                        </span>
                        <div className="text-xs font-bold text-brand-navy leading-snug" data-id="element-5204">
                          {lane}
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              {/* Grid area */}
              <div className="relative" style={{
              width: totalGridW,
              height: gridH
            }} data-id="element-5205">
                {/* Vertical day grid lines + weekend shading */}
                {days.map(d => <div key={d.index} className={`absolute top-0 bottom-0 ${d.isWeekend ? 'bg-slate-50/70' : ''} ${d.isPast ? 'opacity-100' : ''}`} style={{
                left: d.index * DAY_W,
                width: DAY_W,
                borderLeft: d.index === 0 ? '1px solid rgb(226 232 240)' : '1px solid rgb(241 245 249)'
              }} data-id="element-5206" />)}
                {/* Right edge */}
                <div className="absolute top-0 bottom-0 border-r border-slate-200" style={{
                left: totalGridW - 1,
                width: 1
              }} data-id="element-5207" />

                {/* Horizontal lane separators */}
                {laneRows.map(({
                startRow,
                rows
              }, i) => {
                if (i === 0) return null;
                const y = startRow * (ROW_H + ROW_GAP);
                return <div key={i} className="absolute left-0 right-0 border-t border-dashed border-slate-200" style={{
                  top: y
                }} data-id="element-5208" />;
              })}

                {/* TODAY vertical line */}
                <motion.div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{
                left: TODAY_DAY * DAY_W + DAY_W / 2 - 1,
                width: 2
              }} initial={{
                opacity: 0,
                scaleY: 0
              }} whileInView={{
                opacity: 1,
                scaleY: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.4
              }} data-id="element-5209">
                  <div className="w-full h-full bg-brand-coral/60" data-id="element-5210" />
                  <motion.div className="absolute -top-1 -left-1.5 w-4 h-4 rounded-full bg-brand-coral border-2 border-white shadow-md" animate={{
                  boxShadow: ['0 0 0 0 rgba(255,107,74,0.5)', '0 0 0 8px rgba(255,107,74,0)']
                }} transition={{
                  duration: 1.6,
                  repeat: Infinity
                }} data-id="element-5211" />
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-brand-coral text-white text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap" data-id="element-5212">
                    TODAY
                  </div>
                </motion.div>

                {/* Task bars */}
                {(() => {
                let rowIdx = 0;
                const elements: React.ReactElement[] = [];
                laneOrder.forEach(lane => {
                  const laneTasks = laneMap.get(lane)!;
                  laneTasks.forEach(task => {
                    const x = task.startDay * DAY_W + 4;
                    const w = task.duration * DAY_W - 8;
                    const y = rowIdx * (ROW_H + ROW_GAP) + ROW_GAP / 2;
                    const isDone = task.startDay + task.duration <= TODAY_DAY;
                    const isActive = task.startDay <= TODAY_DAY && task.startDay + task.duration > TODAY_DAY;
                    elements.push(<motion.div key={task.id} className={`absolute rounded-lg text-white text-[11px] font-bold px-2.5 flex items-center gap-1.5 shadow-sm ${task.barColor} ${task.star ? 'ring-2 ring-yellow-300' : ''} ${isDone ? 'opacity-70' : ''} group cursor-pointer hover:scale-[1.02] hover:shadow-md transition-transform`} style={{
                      left: x,
                      top: y,
                      width: w,
                      height: ROW_H - ROW_GAP
                    }} initial={{
                      opacity: 0,
                      scaleX: 0,
                      transformOrigin: 'left'
                    }} whileInView={{
                      opacity: 1,
                      scaleX: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.5,
                      delay: 0.3 + rowIdx * 0.07,
                      ease: [0.22, 1, 0.36, 1]
                    }} data-id="element-5213">
                          {task.star && <span data-id="element-5214">★</span>}
                          <span className="truncate" data-id="element-5215">{task.label}</span>
                          {isActive && <motion.span className="ml-auto w-1.5 h-1.5 rounded-full bg-white shrink-0" animate={{
                        opacity: [1, 0.3, 1]
                      }} transition={{
                        duration: 1.2,
                        repeat: Infinity
                      }} data-id="element-5216" />}
                          {isDone && <CheckCircle2Icon className="w-3.5 h-3.5 ml-auto shrink-0" data-id="element-5217" />}
                          {/* Hover tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-30 bg-brand-navy text-white text-xs rounded-lg p-3 shadow-xl min-w-[200px] pointer-events-none" data-id="element-5218">
                            <div className="font-bold mb-1" data-id="element-5219">{task.label}</div>
                            <div className="text-[10px] text-slate-300 mb-1" data-id="element-5220">
                              {task.duration} day
                              {task.duration > 1 ? 's' : ''} · Owner:{' '}
                              {task.owner}
                            </div>
                            {task.deps && task.deps.length > 0 && <div className="text-[10px] text-slate-300" data-id="element-5221">
                                Depends on:{' '}
                                {task.deps.map(id => tasks.find(t => t.id === id)?.id).join(', ')}
                              </div>}
                          </div>
                        </motion.div>);
                    rowIdx++;
                  });
                });
                return elements;
              })()}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4 text-[10px] text-slate-500" data-id="element-5222">
              <div className="flex items-center gap-1.5" data-id="element-5223">
                <span className="w-3 h-3 rounded bg-brand-coral ring-2 ring-yellow-300" data-id="element-5224" />
                <span className="font-bold uppercase tracking-wider" data-id="element-5225">
                  ★ Workflow #1
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-5226">
                <span className="w-1 h-3 rounded bg-brand-coral/60" data-id="element-5227" />
                <span className="font-bold uppercase tracking-wider" data-id="element-5228">
                  Today
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-5229">
                <span className="w-3 h-3 rounded bg-slate-50 border border-slate-200" data-id="element-5230" />
                <span className="font-bold uppercase tracking-wider" data-id="element-5231">
                  Weekend
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-5232">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-500" data-id="element-5233" />
                <span className="font-bold uppercase tracking-wider" data-id="element-5234">Done</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-5235">
                <span className="w-2 h-2 rounded-full bg-brand-navy" data-id="element-5236" />
                <span className="font-bold uppercase tracking-wider" data-id="element-5237">
                  In flight
                </span>
              </div>
              <div className="ml-auto text-slate-400" data-id="element-5238">
                Hover any bar for details
              </div>
            </div>
          </div>
        </div>

        {/* Working principles */}
        <div className="mt-10 grid md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-5239">
          <div className="bg-brand-navy/5 border border-brand-navy/10 rounded-xl p-5" data-id="element-5240">
            <div className="text-xs font-bold text-brand-navy uppercase tracking-wider mb-2" data-id="element-5241">
              Cadence
            </div>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5242">
              Daily stand-up at 9am. Mid-sprint check Tue May 21. Demo Fri May
              28.
            </p>
          </div>
          <div className="bg-brand-coral/5 border border-brand-coral/10 rounded-xl p-5" data-id="element-5243">
            <div className="text-xs font-bold text-brand-coral uppercase tracking-wider mb-2" data-id="element-5244">
              Definition of Done
            </div>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5245">
              Sales Manager uploads a real Lamplighter book of business and
              walks out with a proposal — on staging, end-to-end.
            </p>
          </div>
          <div className="bg-brand-teal/5 border border-brand-teal/10 rounded-xl p-5" data-id="element-5246">
            <div className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-2" data-id="element-5247">
              Out of Scope
            </div>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5248">
              Coordinator, Client portal, Attendee hub, Mux video. All wait
              until after we prove Workflow #1.
            </p>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-5249">
          <CheckCircle2Icon className="w-4 h-4 text-brand-teal" data-id="element-5250" />
          <span data-id="element-5251">Ship by Fri May 28. If it doesn't demo, it doesn't ship.</span>
        </div>
      </div>
    </section>;
}